import React, { createContext, useContext, useState, ReactNode } from 'react';
import {Pathway, PathwayFaux} from '../../types/pathway';

type PathwayContextType = {
  pathway: Pathway|PathwayFaux,
  setPathway: (pathway: Pathway) => void,
}

type AnyFunction = (...args: any[]) => any;
type DeferrablePathwayMethods = Exclude<keyof PathwayFaux, 'trackingAvailable'>;
type EventQueueItem = { method: DeferrablePathwayMethods; args: any[] }

const noop = () => {};

const eventQueue: EventQueueItem[] = [];
const defer = (
  method: EventQueueItem['method'],
  args: EventQueueItem['args']
) => {
  eventQueue.push({ method, args })
}

const pathwayFaux: PathwayFaux = {
  trackingAvailable: false,
  userEnrich: (...args) => defer('userEnrich', args),
  trackUserEnrich: (...args) => defer('trackUserEnrich', args),
  track: (...args) => defer('track', args),
  identify: (...args) => defer('identify', args),
  page: (...args) => defer('page', args),
  configure: (...args) => defer('configure', args),
  createProfile: (...args) => defer('createProfile', args),
}

export const ANALYTICS = {
  CONTACT_TEAM_MEMBER: 'Contact Team Member',
  VIEW_CLOUD_ORGANIZATIONS_AND_SUPPORT: 'View Cloud Organizations and Support',
  VIEW_CLOUD_ORGANIZATIONS_AND_SUPPORT_NEXT_PAGE: 'View Cloud Organizations and Support - Next Page',
  VIEW_CLOUD_ORGANIZATIONS_AND_SUPPORT_PREVIOUS_PAGE: 'View Cloud Organizations and Support - Previous Page',
  CLICK_CLOUD_ORGANISATION: 'Click Cloud Organisation',
  CLICK_CLOUD_ORGANISATION_BILLING: 'Click Cloud Organisation Billing',
  VIEW_ALL_CASES: 'View All Cases',
  CREATE_NEW_CASE: 'Create New Case',
  NEW_CASE_CREATED: 'New Case Created',
  LOAD_RECOMMENDATIONS: 'Load Recommendations',
  RECOMMENDATION_FOLLOW_UP: 'Recommendation Follow Up',
  REQUEST_ASSESSMENT: 'Request Assessment',
  DOWNLOAD_RESOURCE: 'Download Resource',
  DOWNLOAD_RESOURCE_NEXT_PAGE: 'Download Resource - Next Page',
  DOWNLOAD_RESOURCE_PREVIOUS_PAGE: 'Download Resource - Previous Page',
  VIEW_ALL_ACTIVITIES: 'View All Activities',
  CHANGE_ACTIVITY_SEGMENT: 'Change Activity Type',
  VIEW_ACTIVITIES_NEXT_PAGE: 'View Activities - Next Page',
  VIEW_ACTIVITIES_PREVIOUS_PAGE: 'View Activities - Previous Page',
  CLICK_PS_EXPLORE_TRAINING_SERVICES: 'Professional Services - Explore Training Services',
  CLICK_PS_EXPLORE_CONSULTING_SERVICES: 'Professional Services - Explore Consulting Services',
  LOAD_CONTACT_LIST: 'Load Contact List',
  CONTACT_STAFF: 'Contact Staff',
  LOAD_SUMMARY: 'Load Summary',
  CLICK_EXPLORE_SUPPORT_PLANS: 'Click Explore Support Plans',
  SEND_FEEDBACK: 'Send Feedback',
  VIEW_WELCOME_MESSAGE: 'Welcome Message - Display Modal',
  CLICK_WELCOME_MESSAGE_CONTINUE: 'Welcome Message - Continue',
  CLICK_WELCOME_MESSAGE_CLOSE: 'Welcome Message - Close',


}

export const PathwayContext = createContext<PathwayContextType>({
  pathway: pathwayFaux,
  setPathway: noop,
});

export const PathwayProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pathway, setPathway] = useState<Pathway|PathwayFaux>(pathwayFaux);

  const setPathwayAndTriggerQueuedEvents = (pathway: Pathway) => {
    if (pathway && pathway.trackingAvailable) {
      for (const { method, args } of eventQueue.splice(0)) {
        const fn: AnyFunction = pathway[method];
        fn.apply(pathway, args);
      }
    }
    setPathway(pathway)
  }

  return (
    <PathwayContext.Provider value={{
      pathway,
      setPathway: setPathwayAndTriggerQueuedEvents
    }}>
      {children}
    </PathwayContext.Provider>
  );
};

export const usePathway = (): PathwayContextType => {
  const context = useContext(PathwayContext);
  if (context === undefined) {
    throw new Error('usePathway must be used within a PathwayProvider');
  }
  return context;
};
